import React, { MutableRefObject, useState, useEffect } from "react";
import Checkbox from "antd/lib/checkbox";
import Radio from "antd/lib/radio";
import Form, { RuleObject } from "antd/lib/form";
import moment from "moment";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import { AppDatePicker } from "components/inputs";
import { SurveyInput } from "pages/symptoms/surveyForms/surveyInput";
import { SurveyNumberInput } from "pages/symptoms/surveyForms/surveyNumberInput";
import { disableFutureDate } from "helpers/utils";
import {
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerList,
  AnswerOption,
  FormItemStyle,
  AsteriskStyle,
  CardTitle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface BloodPressureSectionProps {
  readonly currentPage: number;
  readonly bloodPressureTimeTaken: string;
  readonly otherTempInput: string;
  setOtherTempInput(input: string): void;
  readonly otherInputRef: MutableRefObject<string>;
  setPostureDuringBP(posture: string): void;
  readonly postureDuringBP: string;
}

export const BloodPressureSection: React.FC<BloodPressureSectionProps> = ({
  currentPage,
  bloodPressureTimeTaken,
  otherTempInput,
  setOtherTempInput,
  otherInputRef,
  setPostureDuringBP,
  postureDuringBP
}) => {
  const [systolicBPTop, setSystolicBPTop] = useState(
    localStorage.getItem(`report${bloodPressureTimeTaken}SystolicBPTop`) || ""
  );

  const [diastolicBPBottom, setDiastolicBPBottom] = useState(
    localStorage.getItem(`report${bloodPressureTimeTaken}DiastolicBPBottom`) ||
      ""
  );
  const [timeStamp, setTimeStamp] = useState("");
  const [date, setDate] = useState<any>();

  useEffect(() => {
    localStorage.setItem("timeStamp", timeStamp);
    localStorage.setItem("timeStampDate", date);

    const newDate = moment(date);
    if (timeStamp === "AM") {
      localStorage.setItem(
        "dateYourBloodPressureWastaken",
        new Date(
          newDate.set({ hour: 9, minute: 0, second: 0 }).toString()
        ).toISOString()
      );
    } else {
      localStorage.setItem(
        "dateYourBloodPressureWastaken",
        new Date(
          newDate.set({ hour: 18, minute: 0, second: 0 }).toString()
        ).toISOString()
      );
    }
  }, [timeStamp]);

  const checkSystolicTop = (_: any, value: number) => {
    if (value) {
      if (value <= 220 && value >= 75) {
        return Promise.resolve();
      }
      return Promise.reject(
        new Error(
          "Reading appears out of range. Please recheck and, if confirmed, call your doctor or go to ER immediately, especially if you have any symptoms."
        )
      );
    }
    return Promise.reject(new Error("This field is required"));
  };

  const checkDiastolicBottom = (_: any, value: number) => {
    if (value) {
      if (value <= 120 && value >= 20) {
        return Promise.resolve();
      }
      return Promise.reject(
        new Error(
          "Reading appears out of range. Please recheck and, if confirmed, call your doctor or go to ER immediately, especially if you have any symptoms."
        )
      );
    }
    return Promise.reject(new Error("This field is required"));
  };

  const validateReportOtherSymptoms = (
    rules: RuleObject,
    value: string,
    callback: (error?: string) => void
  ) => {
    if (
      otherInputRef.current === "" &&
      localStorage.getItem("reportOtherSymptoms")?.includes("Other")
    ) {
      callback("Please enter value for 'Other' field.");
    } else {
      callback();
    }
  };

  return (
    <>
      {currentPage === 1 && (
        <>
          <SurveyNoticeCard />
          <SurveyQuestionCard
            sectionTag="Blood Pressure"
            title="Do you want to report your blood pressure?"
            required
          >
            <FormItemStyle
              name="Report your blood pressure"
              label=""
              rules={[{ required: true }]}
              initialValue={""}
            >
              <Radio.Group
                value={localStorage.getItem("reportYourBloodPressure")}
                onChange={e =>
                  localStorage.setItem(
                    "reportYourBloodPressure",
                    e.target.value
                  )
                }
              >
                <AnswerList>
                  <AnswerOption value="Yes">Yes</AnswerOption>
                  <AnswerOption value="No">No</AnswerOption>
                </AnswerList>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>
        </>
      )}

      {currentPage === 2 && (
        <SurveyQuestionCard
          sectionTag="Blood Pressure"
          title="When did you take your blood pressure?"
          required
        >
          <div
            style={{
              color: "#4a5568",
              marginBottom: "14px",
              fontSize: "14px",
              lineHeight: "1.5"
            }}
          >
            Discuss your target blood pressure with your doctor. PLEASE CALL
            YOUR DOCTOR, OR CALL 911 AND GO TO NEAREST ER FOR ANY SERIOUS CHANGE
            IN YOUR VITAL SIGNS OUTSIDE YOUR USUAL RECOMMENDED RANGE, OR IF YOU
            HAVE ANY ASSOCIATED SYMPTOMS (such as: Chest Pain, Shortness of
            Breath, Headache, Weakness, Dizzyness, Altered Vision, Confusion or
            other neurologic symptoms)
          </div>
          <FormItemStyle
            name="When was blood pressure taken"
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <Radio.Group
              value={localStorage.getItem("whenWasBloodPressureTaken")}
              onChange={e =>
                localStorage.setItem(
                  "whenWasBloodPressureTaken",
                  e.target.value
                )
              }
            >
              <AnswerList>
                <AnswerOption value="Taken today">Taken today</AnswerOption>
                <AnswerOption value="Taken before today">
                  Taken before today
                </AnswerOption>
              </AnswerList>
            </Radio.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 3 && (
        <SurveyQuestionCard
          sectionTag="Blood Pressure"
          title="When was your blood pressure taken?"
          required
        >
          <FormItemStyle
            name="When was your blood pressure taken(Date)"
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <AppDatePicker
              label="Blood Pressure Taken on"
              value={moment(
                localStorage.getItem("dateYourBloodPressureWastaken")
              )}
              onChange={e => {
                if (e) {
                  const newDate = moment(e);
                  setDate(newDate);
                }
              }}
              style={{ width: "250px" }}
              disabledDate={disableFutureDate}
            />
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 4 && (
        <SurveyQuestionCard
          sectionTag="Blood Pressure"
          title="What time was your blood pressure taken?"
          required
        >
          <FormItemStyle
            name="What time was blood pressure taken"
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <Radio.Group
              value={localStorage.getItem("whatTimeWasBloodPressureTaken")}
              onChange={e => {
                setTimeStamp(e.target.value);
                localStorage.setItem(
                  "whatTimeWasBloodPressureTaken",
                  e.target.value
                );
              }}
            >
              <AnswerList>
                <AnswerOption value="AM">AM</AnswerOption>
                <AnswerOption value="PM">PM</AnswerOption>
              </AnswerList>
            </Radio.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 5 && (
        <>
          <SurveyQuestionCard
            sectionTag={`${bloodPressureTimeTaken || ""} Blood Pressure`}
            title={`Report ${bloodPressureTimeTaken} Systolic BP(Top Number)`}
            required
          >
            <div
              style={{
                color: "#4a5568",
                marginBottom: "14px",
                fontSize: "14px",
                lineHeight: "1.5"
              }}
            >
              Discuss your target blood pressure with your doctor. PLEASE CALL
              YOUR DOCTOR, OR CALL 911 AND GO TO NEAREST ER FOR ANY SERIOUS
              CHANGE IN YOUR VITAL SIGNS OUTSIDE YOUR USUAL RECOMMENDED RANGE,
              OR IF YOU HAVE ANY ASSOCIATED SYMPTOMS.
            </div>
            <FormItemStyle
              name={`Report ${bloodPressureTimeTaken} Systolic BP(Top Number)`}
              label=""
              rules={[
                {
                  required: true,
                  validator: checkSystolicTop,
                  validateTrigger: "submit"
                }
              ]}
              initialValue={""}
            >
              <SurveyNumberInput
                value={systolicBPTop}
                placeholder="Your Answer"
                decimalLimit={0}
                id={`lightheadedness_Report ${bloodPressureTimeTaken} Systolic BP(Top Number)`}
                onChange={e => {
                  setSystolicBPTop(e as string);
                  localStorage.setItem(
                    `report${bloodPressureTimeTaken}SystolicBPTop`,
                    e as string
                  );
                }}
                min={0}
                style={{ width: "160px" }}
              />
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard
            title={`Report ${bloodPressureTimeTaken} Diastolic BP(Bottom Number)`}
            required
          >
            <FormItemStyle
              name={`Report ${bloodPressureTimeTaken} Diastolic BP(Bottom Number)`}
              label=""
              rules={[
                {
                  required: true,
                  validator: checkDiastolicBottom,
                  validateTrigger: "submit"
                }
              ]}
              initialValue={""}
            >
              <SurveyNumberInput
                value={diastolicBPBottom}
                placeholder="Your Answer"
                decimalLimit={0}
                id={`lightheadedness_Report ${bloodPressureTimeTaken} Diastolic BP(Bottom Number)`}
                onChange={e => {
                  setDiastolicBPBottom(e as string);
                  localStorage.setItem(
                    `report${bloodPressureTimeTaken}DiastolicBPBottom`,
                    e as string
                  );
                }}
                min={0}
                style={{ width: "160px" }}
              />
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard title="Posture during BP">
            <FormItemStyle name="Posture during BP" label="" initialValue={""}>
              <Radio.Group
                value={postureDuringBP}
                onChange={e => {
                  localStorage.setItem("postureDuringBP", e.target.value);
                  setPostureDuringBP(e.target.value);
                }}
              >
                <AnswerList>
                  <AnswerOption value="Sitting">Sitting</AnswerOption>
                  <AnswerOption value="Standing">Standing</AnswerOption>
                  <AnswerOption value="Lying">Lying</AnswerOption>
                </AnswerList>
              </Radio.Group>
              <div style={{ marginTop: "12px", textAlign: "right" }}>
                <AppButton
                  buttonContent={"Clear Selection"}
                  type={ButtonType.Link}
                  onClick={() => {
                    setPostureDuringBP("");
                    localStorage.setItem("postureDuringBP", "");
                  }}
                />
              </div>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard title="Report other symptoms or comments with BP reading">
            <FormItemStyle
              name="Report other symptoms"
              label=""
              initialValue={""}
              rules={[
                {
                  validator: validateReportOtherSymptoms,
                  validateTrigger: "submit"
                }
              ]}
            >
              <Checkbox.Group
                style={{ width: "100%" }}
                onChange={e =>
                  localStorage.setItem("reportOtherSymptoms", e.toString())
                }
                value={sessionStorage
                  .getItem("reportOtherSymptoms")
                  ?.split(/,/)}
              >
                <AnswerList>
                  <AnswerOption value="Dizzyness or Lightheadedness" isCheckbox>
                    Dizzyness or Lightheadedness
                  </AnswerOption>
                  <AnswerOption value="Weakness or Fatigue" isCheckbox>
                    Weakness or Fatigue
                  </AnswerOption>
                  <AnswerOption value="Other" isCheckbox>
                    <div style={{ width: "100%" }}>
                      <span>Other</span>
                      <SurveyInput
                        value={otherTempInput || otherInputRef.current}
                        placeholder="Please specify"
                        onChange={e => {
                          setOtherTempInput(e.target.value);
                          otherInputRef.current = e.target.value;
                        }}
                        style={{ marginTop: "8px", width: "100%" }}
                      />
                    </div>
                  </AnswerOption>
                </AnswerList>
              </Checkbox.Group>
            </FormItemStyle>
          </SurveyQuestionCard>
        </>
      )}
    </>
  );
};
